import { useCallback } from 'react'
import { useAuthStore } from '@/store/authStore'
import { userApi } from '@/api/endpoints'
import type { CamerasSort } from '@/types'

export const CAMERAS_SORTS: readonly CamerasSort[] = ['latest_event', 'number']
export const DEFAULT_CAMERAS_SORT: CamerasSort = 'latest_event'

export function isCamerasSort(value: unknown): value is CamerasSort {
  return typeof value === 'string' && (CAMERAS_SORTS as readonly string[]).includes(value)
}

/**
 * #277 — the signed-in user's order of the Cameras page.
 *
 * The value lives on the profile (`cameras_sort`), which `useLanguageSync` pulls into the
 * auth store at sign-in, so it follows the user to any browser. Changing it updates the
 * store at once (the list re-queries) and saves to the profile the way `theme` does; a
 * failed save keeps the choice for this session only.
 */
export function useCamerasSort(): [CamerasSort, (value: CamerasSort) => void] {
  const saved = useAuthStore((s) => s.user?.cameras_sort)
  const updateUser = useAuthStore((s) => s.updateUser)
  const sort = isCamerasSort(saved) ? saved : DEFAULT_CAMERAS_SORT

  const setSort = useCallback((value: CamerasSort) => {
    updateUser({ cameras_sort: value })
    userApi.patchProfile({ cameras_sort: value }).catch(() => {})
  }, [updateUser])

  return [sort, setSort]
}
